import { z } from 'zod';
import { cardReadEvidenceSchema, canteenOutboxEventSchema, issuanceCorrectionSchema, issuanceLineSchema, issuanceSchema, issueMealsCommandSchema, reservationBatchAudienceSchema, reservationBatchCommitRequestSchema, reservationBatchJobSchema, reservationBatchJobDetailsSchema, reservationBatchPreviewRequestSchema, reservationBatchPreviewSchema, reservationBatchSelectionSchema, reservationAdjustmentSchema, reservationLineSchema, reservationSchema, syncCommandBatchResultSchema, syncCommandBatchSchema, syncCommandReceiptSchema, syncCommandResultSchema, } from './canteen.js';
const asJsonSchema = (schema) => z.toJSONSchema(schema, { target: 'draft-2020-12' });
export const canteenSyncOpenApiDocument = {
    openapi: '3.1.0',
    info: {
        title: 'SAD5 Stołówka Sync API',
        version: '1.0.0',
        description: 'Wersjonowany kontrakt rezerwacji, wydań offline i uzgadniania terminali.',
    },
    servers: [{ url: '/api/v1' }],
    tags: [{ name: 'Canteen reservations' }, { name: 'Canteen sync' }],
    paths: {
        '/reservation-batches/preview': {
            post: {
                tags: ['Canteen reservations'],
                operationId: 'previewReservationBatchV1',
                summary: 'Wylicza odbiorców, konflikty i skutek finansowy operacji masowej.',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: {
                                $ref: '#/components/schemas/ReservationBatchPreviewRequest',
                            },
                        },
                    },
                },
                responses: {
                    '200': {
                        description: 'Niezmienny, czasowy podgląd operacji masowej.',
                        content: {
                            'application/json': {
                                schema: {
                                    $ref: '#/components/schemas/ReservationBatchPreview',
                                },
                            },
                        },
                    },
                },
            },
        },
        '/reservation-batches/commit': {
            post: {
                tags: ['Canteen reservations'],
                operationId: 'commitReservationBatchV1',
                summary: 'Uruchamia zatwierdzony podgląd jako wznawialne zadanie.',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: {
                                $ref: '#/components/schemas/ReservationBatchCommitRequest',
                            },
                        },
                    },
                },
                responses: {
                    '202': {
                        description: 'Zadanie zostało przyjęte lub zwrócono wcześniejszy receipt.',
                        content: {
                            'application/json': {
                                schema: { $ref: '#/components/schemas/ReservationBatchJob' },
                            },
                        },
                    },
                    '409': {
                        description: 'Podgląd wygasł albo jego audytorium uległo zmianie.',
                    },
                },
            },
        },
        '/reservation-batches/{jobId}': {
            get: {
                tags: ['Canteen reservations'],
                operationId: 'getReservationBatchJobV1',
                summary: 'Zwraca postęp i wynik wznawialnej operacji masowej.',
                parameters: [
                    {
                        name: 'jobId',
                        in: 'path',
                        required: true,
                        schema: { type: 'string', format: 'uuid' },
                    },
                ],
                responses: {
                    '200': {
                        description: 'Bieżący stan zadania.',
                        content: {
                            'application/json': {
                                schema: { $ref: '#/components/schemas/ReservationBatchJob' },
                            },
                        },
                    },
                },
            },
        },
        '/reservation-batches/{jobId}/items': {
            get: {
                tags: ['Canteen reservations'],
                operationId: 'getReservationBatchJobItemsV1',
                summary: 'Zwraca indywidualny wynik każdej pozycji zadania.',
                parameters: [
                    {
                        name: 'jobId',
                        in: 'path',
                        required: true,
                        schema: { type: 'string', format: 'uuid' },
                    },
                ],
                responses: {
                    '200': {
                        description: 'Pełny, uporządkowany raport pozycji.',
                        content: {
                            'application/json': {
                                schema: {
                                    $ref: '#/components/schemas/ReservationBatchJobDetails',
                                },
                            },
                        },
                    },
                },
            },
        },
        '/sync/commands': {
            post: {
                tags: ['Canteen sync'],
                operationId: 'submitCanteenSyncCommandsV1',
                summary: 'Przyjmuje niezależnie rozstrzyganą paczkę komend terminala.',
                requestBody: {
                    required: true,
                    content: {
                        'application/json': {
                            schema: { $ref: '#/components/schemas/SyncCommandBatch' },
                        },
                    },
                },
                responses: {
                    '200': {
                        description: 'Wynik każdej komendy wraz z kursorem zmian.',
                        content: {
                            'application/json': {
                                schema: {
                                    $ref: '#/components/schemas/SyncCommandBatchResult',
                                },
                            },
                        },
                    },
                    '409': {
                        description: 'Klucz idempotencji został użyty z innym payloadem.',
                        content: {
                            'application/json': {
                                schema: {
                                    type: 'object',
                                    required: ['code', 'message', 'correlationId'],
                                    properties: {
                                        code: { const: 'idempotency_key_reused' },
                                        message: { type: 'string' },
                                        correlationId: { type: 'string' },
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    },
    components: {
        securitySchemes: {
            openId: {
                type: 'openIdConnect',
                openIdConnectUrl: '/realms/{institution}/.well-known/openid-configuration',
            },
        },
        schemas: {
            Reservation: asJsonSchema(reservationSchema),
            ReservationLine: asJsonSchema(reservationLineSchema),
            ReservationAdjustment: asJsonSchema(reservationAdjustmentSchema),
            ReservationBatchAudience: asJsonSchema(reservationBatchAudienceSchema),
            ReservationBatchSelection: asJsonSchema(reservationBatchSelectionSchema),
            ReservationBatchPreviewRequest: asJsonSchema(reservationBatchPreviewRequestSchema),
            ReservationBatchPreview: asJsonSchema(reservationBatchPreviewSchema),
            ReservationBatchCommitRequest: asJsonSchema(reservationBatchCommitRequestSchema),
            ReservationBatchJob: asJsonSchema(reservationBatchJobSchema),
            ReservationBatchJobDetails: asJsonSchema(reservationBatchJobDetailsSchema),
            CardReadEvidence: asJsonSchema(cardReadEvidenceSchema),
            Issuance: asJsonSchema(issuanceSchema),
            IssuanceLine: asJsonSchema(issuanceLineSchema),
            IssuanceCorrection: asJsonSchema(issuanceCorrectionSchema),
            IssueMealsCommand: asJsonSchema(issueMealsCommandSchema),
            SyncCommandReceipt: asJsonSchema(syncCommandReceiptSchema),
            SyncCommandResult: asJsonSchema(syncCommandResultSchema),
            SyncCommandBatch: asJsonSchema(syncCommandBatchSchema),
            SyncCommandBatchResult: asJsonSchema(syncCommandBatchResultSchema),
            CanteenOutboxEvent: asJsonSchema(canteenOutboxEventSchema),
        },
    },
    security: [{ openId: [] }],
};
